import axios from "axios"
import { useMemo } from "react"
import { useSelector } from "react-redux"

import {
  isAccountOwner,
  usesAccountOwnerHeader,
} from "../pages/users/accountRole"
import {
  ACTIVE_ACCOUNT_ID_STORAGE_KEY,
  selectActiveAccountId,
  selectProfile,
  selectToken,
} from "../pages/users/reducer"
import { attachCustomerAuthInterceptor } from "./customerAuthSession"

export const axiosClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
})

function buildAccountAccessHeaders(token, profile, activeAccountIdOverride) {
  const headers = {
    Authorization: `Token ${token}`,
  }
  const storedAccountId = localStorage.getItem(ACTIVE_ACCOUNT_ID_STORAGE_KEY)
  const activeAccountId =
    activeAccountIdOverride ??
    profile?.active_account?.id ??
    (storedAccountId ? Number(storedAccountId) : null)

  // Explicit owners never send the parent header. Concierge/EC/member do.
  // On cold start (role not loaded yet), keep sending the stored account id so
  // switch + reload does not fall back to the first membership.
  const sendParentAccountHeader =
    Boolean(activeAccountId) &&
    !isAccountOwner(profile) &&
    (usesAccountOwnerHeader(profile) || Boolean(storedAccountId))

  if (sendParentAccountHeader) {
    headers["X-Account-Owner-Id"] = String(activeAccountId)
  }
  return headers
}

export function getAuthorizedAxiosClient(token, profile = null) {
  return attachCustomerAuthInterceptor(
    axios.create({
      baseURL: process.env.REACT_APP_API_URL,
      headers: buildAccountAccessHeaders(token, profile),
    })
  )
}

export function useAuthorizedAxiosClient(baseURL) {
  const token = useSelector(selectToken)
  const profile = useSelector(selectProfile)
  const activeAccountId = useSelector(selectActiveAccountId)
  const resolvedBaseUrl = baseURL || process.env.REACT_APP_API_URL
  return useMemo(
    () =>
      attachCustomerAuthInterceptor(
        axios.create({
          baseURL: resolvedBaseUrl,
          headers: buildAccountAccessHeaders(token, profile, activeAccountId),
        })
      ),
    [
      token,
      resolvedBaseUrl,
      activeAccountId,
      profile?.role,
      profile?.is_onebase_staff,
      profile?.is_concierge,
    ]
  )
}

export const MULTIPART_HEADERS = {
  headers: { "Content-Type": "multipart/form-data" },
}
